import React, { useMemo } from 'react'
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts'

interface MaxGaugeProps {
  current?: number
  max?: number
  isLoading?: boolean
  error?: string | null
}

/**
 * MaxGauge displays a circular gauge showing current/900 messages
 * Color states: green <600, yellow 600-800, red >800
 */
const MaxGauge: React.FC<MaxGaugeProps> = ({
  current = 0,
  max = 900,
  isLoading = false,
  error = null,
}) => {
  const gaugeData = useMemo(() => {
    const safeValue = Math.max(current, 0)
    // For the visual gauge, cap at max so the pie chart doesn't break
    const displayValue = Math.min(safeValue, max)
    const remaining = max - displayValue
    return [
      { name: 'Used', value: displayValue },
      { name: 'Remaining', value: remaining },
    ]
  }, [current, max])

  // Determine color based on current value (not clamped)
  const getGaugeColor = (value: number): string => {
    if (value < 600) return '#A3F5D7' // Green
    if (value < 800) return '#FFA31A' // Yellow
    return '#D63329' // Red
  }

  const safeValue = Math.max(current, 0)
  const gaugeColor = getGaugeColor(safeValue)
  const percentage = Math.round((safeValue / max) * 100)

  // Loading state
  if (isLoading) {
    return (
      <div
        className="w-full h-80 flex items-center justify-center bg-[#F3F3F3] rounded-puzzle"
        role="status"
        aria-label="Loading gauge"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#FF5433] border-t-transparent mx-auto mb-2" />
          <p className="text-[#666666] text-sm">Loading gauge...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div
        className="w-full h-80 flex items-center justify-center bg-[#FFEBEB] rounded-puzzle border border-[#D63329]"
        role="alert"
      >
        <div className="text-center">
          <p className="text-[#D63329] text-sm font-bold">Error loading gauge</p>
          <p className="text-[#666666] text-xs mt-1">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-80 flex flex-col items-center justify-center">
      <div className="relative w-48 h-48">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={gaugeData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={80}
              startAngle={180}
              endAngle={0}
              dataKey="value"
              isAnimationActive={true}
            >
              <Cell fill={gaugeColor} />
              <Cell fill="#E8E8E8" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        {/* Center text overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-3xl font-bold text-[#000000]">{safeValue.toLocaleString()}</div>
          <div className="text-xs text-[#666666]">/ {max.toLocaleString()}</div>
        </div>
      </div>
      {/* Status text below gauge */}
      <div className="mt-6 text-center">
        <div className="text-2xl font-bold" style={{ color: gaugeColor }}>
          {percentage}%
        </div>
        <div className="text-sm text-[#666666] mt-1">
          {safeValue < 600 && 'Good'}
          {safeValue >= 600 && safeValue < 800 && 'Warning'}
          {safeValue >= 800 && 'Critical'}
        </div>
      </div>
    </div>
  )
}

export default MaxGauge
